<div class="container" *ngIf="user">
  <div class="account-header flex-row flex-center-y margin-bottom-40">
    <div class="avatar-circle" data-testid="avatar-circle">
      <span class="initials" data-testid="avatar-initials">{{ user.USER.FIRST_NAME.charAt(0) }}{{ user.USER.LAST_NAME.charAt(0) }}</span>
    </div>
    <div class="header-detail">
      <h1>My Account</h1>
      <button class="btn btn-secondary btn-small btn-noborder logout-btn" data-testid="btn-logout" (click)="logout()">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-linecap="round" stroke-linejoin="round" stroke-width="2" width="14" height="14">
          <path d="M14 8v-2a2 2 0 0 0 -2 -2h-7a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h7a2 2 0 0 0 2 -2v-2"></path>
          <path d="M9 12h12l-3 -3m0 6l3 -3"></path>
        </svg>
        Logout
      </button>
    </div>
  </div>

  <!-- Personal Information Section -->
  <div class="account-card">
    <div class="account-section-header">
      <h2>Personal Information</h2>
      <div class="account-section-actions">
        <ng-container *ngIf="!editingPersonal; else personalEditActions">
          <button
            class="btn btn-secondary btn-small"
            data-testid="btn-edit-personal"
            (click)="startEditPersonal()"
          >Edit</button>
        </ng-container>
        <ng-template #personalEditActions>
          <button
            class="btn btn-secondary btn-small margin-right-10"
            data-testid="btn-cancel-personal"
            [disabled]="isSaving"
            (click)="cancelEditPersonal()"
          >Cancel</button>
          <button
            class="btn btn-primary btn-small"
            data-testid="btn-save-personal"
            [disabled]="isSaving"
            (click)="savePersonal()"
          >Save</button>
        </ng-template>
      </div>
    </div>

    <div class="form-group">
      <div class="form-row two-item">
        <div class="form-field">
          <label for="firstName">First Name</label>
          <input
            type="text"
            id="firstName"
            data-testid="input-my-account-first-name"
            [value]="editingPersonal ? personalDraft.FIRST_NAME : user.USER.FIRST_NAME"
            [disabled]="!editingPersonal"
            (input)="personalDraft.FIRST_NAME = $any($event.target).value"
          >
        </div>

        <div class="form-field">
          <label for="lastName">Last Name</label>
          <input
            type="text"
            id="lastName"
            data-testid="input-my-account-last-name"
            [value]="editingPersonal ? personalDraft.LAST_NAME : user.USER.LAST_NAME"
            [disabled]="!editingPersonal"
            (input)="personalDraft.LAST_NAME = $any($event.target).value"
          >
        </div>
      </div>

      <div class="form-row one-item">
        <div class="form-field">
          <label for="email">Email</label>
          <input
            type="email"
            id="email"
            data-testid="input-my-account-email"
            [value]="editingPersonal ? personalDraft.EMAIL : user.USER.EMAIL"
            [disabled]="!editingPersonal"
            (input)="personalDraft.EMAIL = $any($event.target).value"
          >
        </div>
      </div>
    </div>
  </div>

  <!-- Organization Section -->
  <div class="account-card margin-top-20">
    <div class="account-section-header">
      <h2>Organization Information</h2>
      <div class="account-section-actions" *ngIf="canEditOrganizationSection">
        <ng-container *ngIf="!editingOrganization; else orgEditActions">
          <button
            class="btn btn-secondary btn-small"
            data-testid="btn-edit-organization"
            (click)="startEditOrganization()"
          >Edit</button>
        </ng-container>
        <ng-template #orgEditActions>
          <button
            class="btn btn-secondary btn-small margin-right-10"
            data-testid="btn-cancel-organization"
            [disabled]="isSaving"
            (click)="cancelEditOrganization()"
          >Cancel</button>
          <button
            class="btn btn-primary btn-small"
            data-testid="btn-save-organization"
            [disabled]="isSaving"
            (click)="saveOrganization()"
          >Save</button>
        </ng-template>
      </div>
    </div>

    <div class="form-group">

      <div class="form-row two-item">
        <div class="form-field">
          <label for="organizationGroupName">Organization Group</label>

          <!-- Super admin editing: org group dropdown -->
          <ng-container *ngIf="canEditOrganizationSection && editingOrganization; else orgGroupReadonly">
            <select
              id="organizationGroupName"
              data-testid="input-my-account-organization-group-name"
              [(ngModel)]="orgDraft.ORGANIZATION_GROUP_ID"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="onOrgGroupChange()"
              [disabled]="loadingOrgGroups"
            >
              <option [ngValue]="null" disabled>
                {{ loadingOrgGroups ? 'Loading...' : 'Select a group' }}
              </option>
              <option *ngFor="let g of organizationGroups" [ngValue]="g.GROUP_ID">{{ g.GROUP_NAME }}</option>
            </select>
          </ng-container>
          <ng-template #orgGroupReadonly>
            <input
              type="text"
              id="organizationGroupName"
              data-testid="input-my-account-organization-group-name"
              [value]="(displayOrganizationGroup$ | async) | displayOrgName"
              disabled
            >
          </ng-template>
        </div>

        <div class="form-field">
          <label for="role">Role</label>

          <!-- Super admin editing: dropdown -->
          <ng-container *ngIf="canEditOrganizationSection && editingOrganization; else roleReadonly">
            <select
              id="role"
              data-testid="input-my-account-role"
              [(ngModel)]="orgDraft.ROLE"
              [ngModelOptions]="{ standalone: true }"
            >
              <option *ngFor="let r of roleOptions" [ngValue]="r">{{ r }}</option>
            </select>
          </ng-container>
          <ng-template #roleReadonly>
            <input
              type="text"
              id="role"
              data-testid="input-my-account-role"
              [value]="user.USER.ROLE"
              disabled
            >
          </ng-template>
        </div>
      </div>

      <div class="form-row one-item">
        <div class="form-field">
          <label for="organizationName">
            {{ (isGroupLevelUser$ | async) ? 'Active Firm' : 'Organization' }}
          </label>

          <!-- Super admin editing: org dropdown filtered by selected group; allows no-organization. -->
          <ng-container *ngIf="canEditOrganizationSection && editingOrganization; else orgNonEditView">
            <select
              id="organizationName"
              data-testid="input-my-account-organization-name"
              [(ngModel)]="orgDraft.ORGANIZATION_ID"
              [ngModelOptions]="{ standalone: true }"
              [disabled]="!orgDraft.ORGANIZATION_GROUP_ID || loadingOrgs"
            >
              <option [ngValue]="null">
                {{ loadingOrgs ? 'Loading...' : (orgDraft.ORGANIZATION_GROUP_ID ? '— No organization —' : 'Select a group first') }}
              </option>
              <option
                *ngFor="let org of filteredOrgs"
                [ngValue]="org.ORGANIZATION_ID"
              >{{ org.ORGANIZATION_NAME }}</option>
            </select>
          </ng-container>

          <!-- Non-super-admin / non-edit view -->
          <ng-template #orgNonEditView>
            <!-- Supervisor: active-firm switcher with explicit "no firm" option. Reflects activeOrg, not the stale home org. -->
            <ng-container *ngIf="isSupervisor && accessibleOrgs.length > 0; else orgReadonly">
              <select
                id="organizationName"
                data-testid="input-my-account-organization-name"
                (change)="switchActiveOrg($event)"
              >
                <option
                  *ngFor="let org of accessibleOrgs"
                  [value]="org.ORGANIZATION_ID"
                  [selected]="org.ORGANIZATION_ID === (displayOrganization$ | async)?.ORGANIZATION_ID"
                >{{ org.ORGANIZATION_NAME }}</option>
              </select>
            </ng-container>
            <ng-template #orgReadonly>
              <input
                type="text"
                id="organizationName"
                data-testid="input-my-account-organization-name"
                [value]="(displayOrganization$ | async) | displayOrgName"
                disabled
              >
            </ng-template>
          </ng-template>
        </div>

      </div>

    </div>
  </div>

  <!-- Account Information (read-only) -->
  <div class="account-card margin-top-20">
    <h2 class="margin-bottom-20">Account Information</h2>
    <div class="form-group">
      <div class="form-row one-item">
        <div class="form-field">
          <label for="userId">User ID</label>
          <input
            type="text"
            id="userId"
            data-testid="input-my-account-user-id"
            [value]="user.USER.USER_ID"
            disabled
          >
        </div>
      </div>

      <div class="form-row one-item">
        <div class="form-field">
          <label for="cognitoId">Access ID</label>
          <input
            type="text"
            id="cognitoId"
            data-testid="input-my-account-cognito-id"
            [value]="user.USER.COGNITO_ID"
            disabled
          >
        </div>
      </div>

      <div class="form-row two-item">
        <div class="form-field">
          <label for="createdAt">Created At</label>
          <input
            type="text"
            id="createdAt"
            data-testid="input-my-account-created-at"
            [value]="formatDate(user.USER.CREATED_AT)"
            disabled
          >
        </div>

        <div class="form-field">
          <label for="updatedAt">Last Updated</label>
          <input
            type="text"
            id="updatedAt"
            data-testid="input-my-account-updated-at"
            [value]="formatDate(user.USER.UPDATED_AT)"
            disabled
          >
        </div>
      </div>
    </div>
  </div>

  <!-- User Administration (dev / cache inspection) -->
  <div class="account-card margin-top-20">
    <h2 class="margin-bottom-20">User Administration</h2>
    <div class="form-group">
      <div class="form-row one-item">
        <div class="form-field">
          <label>Cached User Details</label>
          <pre style="max-height: 240px; overflow: auto; background:#f9fafb; padding:12px; border-radius:6px; border:1px solid #eee;">
{{ cachedUserDetails | json }}
          </pre>
        </div>
      </div>

      <div class="form-row two-item">
        <div class="form-field">
          <label>Cached At</label>
          <input
            type="text"
            [value]="formatCachedAt(cachedUserCachedAt)"
            disabled
          >
        </div>

        <div class="form-field flex-row flex-center-y" style="align-items: flex-end;">
          <button class="btn btn-secondary" data-testid="btn-clear-cache" (click)="clearCacheAndRefetch()">Clear cache and re-fetch</button>
        </div>
      </div>
    </div>
  </div>


</div>

<div class="loading-container" *ngIf="!user">
  <div class="loading-message">Loading user information...</div>
</div>
